import { useState, useMemo, useEffect, useCallback } from 'react';

/**
 * @file useProjectMap.js
 * @description Custom hook for managing state and logic of the project overview map.
 * Handles filtering, grouping, searching, and selection of projects.
 * 
 * @param {Object} params
 * @param {Array} params.projects - List of all projects from parent
 * @param {Object} params.focusedProject - Project to focus on (from props)
 */
export function useProjectMap({ projects, focusedProject }) {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [projectSearch, setProjectSearch] = useState('');
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({
        active: true,
        paused: true,
        completed: false,
        archived: false
    });

    // Projects that have GPS data
    const projectsWithGps = useMemo(() =>
        projects.filter(p => p.latitude && p.longitude),
        [projects]);

    // Initial selection of all projects with GPS
    useEffect(() => {
        const ids = projectsWithGps.map(p => p.id);
        setSelectedProjectIds(ids);
    }, [projectsWithGps]);

    // Filter projects for display on map based on selection
    const filteredForMap = useMemo(() =>
        projectsWithGps.filter(p => selectedProjectIds.includes(p.id)),
        [projectsWithGps, selectedProjectIds]);

    // Ensure focused project is selected if it becomes focused
    useEffect(() => {
        if (focusedProject && !selectedProjectIds.includes(focusedProject.id)) {
            setSelectedProjectIds(prev => [...prev, focusedProject.id]);
        }
    }, [focusedProject]);

    // Filter projects for the sidebar based on search
    const sidebarProjects = useMemo(() => {
        if (!projectSearch) return projectsWithGps;
        const lower = projectSearch.toLowerCase();
        return projectsWithGps.filter(p =>
            p.name.toLowerCase().includes(lower) ||
            (p.project_number && p.project_number.toLowerCase().includes(lower))
        );
    }, [projectsWithGps, projectSearch]);

    // Group sidebar projects by status
    const groupedProjects = useMemo(() => {
        const groups = {
            active: [],
            paused: [],
            completed: [],
            archived: []
        };
        sidebarProjects.forEach(p => {
            const status = p.status || 'active';
            if (groups[status]) {
                groups[status].push(p);
            }
        });
        return groups;
    }, [sidebarProjects]);

    /**
     * Handlers for sidebar interactions
     */
    const toggleGroup = useCallback((status) => {
        setExpandedGroups(prev => ({ ...prev, [status]: !prev[status] }));
    }, []);

    const toggleGroupProjects = useCallback((status, projectsInGroup) => {
        const groupIds = projectsInGroup.map(p => p.id);
        const allSelected = groupIds.every(id => selectedProjectIds.includes(id));
        
        if (allSelected) {
            setSelectedProjectIds(prev => prev.filter(id => !groupIds.includes(id)));
        } else {
            setSelectedProjectIds(prev => [...new Set([...prev, ...groupIds])]);
        }
    }, [selectedProjectIds]);

    const toggleProject = useCallback((id) => {
        setSelectedProjectIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    }, []);

    const selectAll = useCallback(() => {
        setSelectedProjectIds(projectsWithGps.map(p => p.id));
    }, [projectsWithGps]);

    const deselectAll = useCallback(() => {
        setSelectedProjectIds([]);
    }, []);

    return {
        isSidebarOpen, setIsSidebarOpen,
        projectSearch, setProjectSearch,
        selectedProjectIds,
        expandedGroups,
        projectsWithGps,
        filteredForMap,
        groupedProjects,
        sidebarProjects,
        toggleGroup,
        toggleGroupProjects,
        toggleProject,
        selectAll,
        deselectAll
    };
}
